import {Injectable, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import {AppAuthService} from '@app/auth';
import {FirebaseMessaging} from '@capacitor-firebase/messaging';
import {Subscription} from 'rxjs';
import {ApiService} from './api.service';
import {PlatformService} from './platform.service';
import {SessionService} from './session.service';

@Injectable({providedIn: 'root'})
export class PushNotificationsService implements OnDestroy {
	private listenersRegistered = false;
	private registering = false;
	private lastSyncedToken: string | null = null;
	private authSubscription?: Subscription;
	private permissionSubscription?: Subscription;

	constructor(
		private platformService: PlatformService,
		private apiService: ApiService,
		private authService: AppAuthService,
		private router: Router,
		private sessionService: SessionService,
	) {
	}

	ngOnDestroy(): void {
		this.authSubscription?.unsubscribe();
		this.permissionSubscription?.unsubscribe();
	}

	initAfterLogin(): void {
		if (!this.platformService.isMobileApp()) {
			return;
		}

		this.authSubscription?.unsubscribe();
		this.authSubscription = this.authService.onTokenChange().subscribe((token) => {
			if (!token.isValid()) {
				this.lastSyncedToken = null;
				return;
			}
			void this.registerFirebaseMessaging();
		});
	}

	private async registerFirebaseMessaging(): Promise<void> {
		if (this.registering) {
			return;
		}

		this.registering = true;

		try {
			await this.registerListeners();

			const supported = await FirebaseMessaging.isSupported();
			if (!supported.isSupported) {
				console.warn('[Push] Firebase Messaging non supporté sur cette plateforme');
				return;
			}

			let permission = await FirebaseMessaging.checkPermissions();
			console.info('[Push] Permission actuelle:', permission.receive);

			if (permission.receive === 'prompt') {
				permission = await FirebaseMessaging.requestPermissions();
				console.info('[Push] Permission après demande:', permission.receive);
			}

			if (permission.receive !== 'granted') {
				console.warn('[Push] Permission refusée — token FCM impossible');
				return;
			}

			await this.syncFirebaseToken();
		} finally {
			this.registering = false;
		}
	}

	private async syncFirebaseToken(): Promise<void> {
		try {
			const {token} = await FirebaseMessaging.getToken();
			if (!token) {
				console.warn('[Push] getToken() a retourné un token vide');
				return;
			}

			if (token === this.lastSyncedToken) {
				return;
			}

			console.info('[Push] Token FCM obtenu:', token.slice(0, 12) + '…');
			this.sendTokenToApi(token);
			this.lastSyncedToken = token;
		} catch (error) {
			console.error('[Push] Échec récupération token FCM:', error);
		}
	}

	private sendTokenToApi(token: string): void {
		this.apiService.addNotificationToken(token, this.platformService.getPlatform()).subscribe({
			next: () => console.info('[Push] Token enregistré sur l\'API'),
			error: (error) => console.error('[Push] Échec envoi token API:', error),
		});
	}

	private async registerListeners(): Promise<void> {
		if (this.listenersRegistered) {
			return;
		}

		this.listenersRegistered = true;

		await FirebaseMessaging.addListener('tokenReceived', (event) => {
			if (event.token && event.token !== this.lastSyncedToken) {
				console.info('[Push] tokenReceived');
				this.sendTokenToApi(event.token);
				this.lastSyncedToken = event.token;
			}
		});

		await FirebaseMessaging.addListener('notificationReceived', () => {
			// In-app notifications are handled by the existing header notification center.
		});

		await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
			const data = event.notification?.data as Record<string, string> | undefined;
			const url = data?.['url'];
			if (url) {
				this.openNotificationUrl(url);
			}
		});
	}

	private openNotificationUrl(url: string): void {
		const go = () => {
			void this.router.navigateByUrl(url);
		};
		if (this.sessionService.arePermissionsLoaded()) {
			go();
			return;
		}
		this.permissionSubscription?.unsubscribe();
		this.permissionSubscription = this.sessionService.getPermissionChangeEmitter().subscribe(() => {
			if (!this.sessionService.arePermissionsLoaded()) {
				return;
			}
			this.permissionSubscription?.unsubscribe();
			this.permissionSubscription = undefined;
			go();
		});
	}
}
